import {
    CREATED,
    PROCESSING,
    SHIPPED,
    IN_TRANSIT,
    DELIVERED,
    CANCELLED,
    TRANSFERRED,
    SCHEDULED,
    RELEASED,
    RETURN_REQUESTED,
    RETURN_INTRANSIT,
    RETURN_PROCESSING,
    RETURN_ACCEPTED,
    RETURN_RECEIVED,
    RETURN_REFUNDED,
    RETURN_REFUSED,
    RETURN_PARTIALLY_REFUNDED
} from './constants'
import { IOrderTrackingEvent, IOrderTrackingData, TimelineOptions } from './interfaces'

const TIMELINES = (messages: any, options: TimelineOptions) => ({
    STANDARD: (trackingEvents: Array<IOrderTrackingEvent>) => [
        {
            status: CREATED,
            title: messages.orderStatus.ordered(),
            description: (info: any) => messages.orderStatusDesc.ordered(info),
            deliveryTimeInfo: messages.orderTracking.estimatedDeliveryDateInfo(),
            events: [],
            dotStatus: 'success',
        },
        {
            status: PROCESSING,
            title: messages.orderStatus.processing(),
            description: (info: any) => messages.orderStatusDesc.processing(info),
            deliveryTimeInfo: messages.orderTracking.estimatedDeliveryDateInfo(),
            events: getProcessingEventsByCountry(messages, options),
            dotStatus: 'success',
        },
        {
            status: SHIPPED,
            title: messages.orderStatus.dispatched(),
            description: (info: any) => messages.orderStatusDesc.dispatched(info),
            deliveryTimeInfo: messages.orderTracking.estimatedDeliveryDateInfo(),
            events: [],
            dotStatus: 'success',
        },
        {
            status: IN_TRANSIT,
            title: messages.orderStatus.inTransit(),
            description: (info: any) => messages.orderStatusDesc.inTransit(info),
            events: trackingEvents,
            dotStatus: 'success',
        },
        {
            status: DELIVERED,
            title: messages.orderStatus.delivered(),
            description: (info: any) => messages.orderStatusDesc.delivered(info),
            events: [],
            dotStatus: 'none',
        },
    ],
    PREMIER: () => [
        {
            status: CREATED,
            title: messages.orderStatus.ordered(),
            description: (info: any) => messages.orderStatusDesc.ordered(info),
            deliveryTimeInfo: messages.orderTracking.estimatedDeliveryDateInfoPremier(),
            events: [],
            dotStatus: 'success',
        },
        {
            status: PROCESSING,
            title: messages.orderStatus.processing(),
            description: (info: any) => messages.orderStatusDesc.processing(info),
            deliveryTimeInfo: messages.orderTracking.estimatedDeliveryDateInfoPremier(),
            events: getProcessingEventsByCountry(messages, options),
            dotStatus: 'success',
        },
        {
            status: SHIPPED,
            title: messages.orderStatus.dispatched(),
            description: (info: any) => messages.orderStatusDesc.dispatched(info),
            deliveryTimeInfo: messages.orderTracking.estimatedDeliveryDateInfoPremier(),
            events: [],
            dotStatus: 'none',
        },
    ],
    RETURNS: (trackingEvents: Array<IOrderTrackingEvent>) => [
        {
            status: RETURN_REQUESTED,
            title: messages.orderStatus.returnRequested(),
            description: (_: any) => '',
            deliveryTimeInfo: messages.returnTracking.estimatedRefundDateInfo(),
            events: [],
            dotStatus: 'success',
        },
        {
            status: RETURN_INTRANSIT,
            title: messages.orderStatus.returnInTransit(),
            description: (_: any) => '',
            deliveryTimeInfo: messages.returnTracking.estimatedRefundDateInfo(),
            events: trackingEvents,
            dotStatus: 'success',
        },
        {
            status: RETURN_PROCESSING,
            title: messages.orderStatus.returnProcessing(),
            description: (_: any) => '',
            deliveryTimeInfo: messages.returnTracking.estimatedRefundDateInfo(),
            events: getReturnProcessingEvents(messages, options),
            dotStatus: 'success',
        },
        {
            status: options?.isPartialReturnRefused ? RETURN_PARTIALLY_REFUNDED : RETURN_REFUNDED,
            title: options?.isPartialReturnRefused ? messages.orderStatus.partiallyReturnRefunded() : messages.orderStatus.returnRefunded(),
            description: (_: any) => '',
            deliveryTimeInfo: messages.returnTracking.estimatedRefundDateInfo(),
            events: [],
            dotStatus: 'none',
        }
    ],
    RETURNS_WITHOUT_TRACKING: () => [
        {
            status: RETURN_REQUESTED,
            title: messages.orderStatus.returnRequested(),
            description: (_: any) => '',
            deliveryTimeInfo: messages.returnTracking.estimatedRefundDateInfo(),
            events: [],
            dotStatus: 'success',
        },
        {
            status: RETURN_ACCEPTED,
            title: messages.orderStatus.returnAccepted(),
            description: (_: any) => '',
            deliveryTimeInfo: messages.returnTracking.estimatedRefundDateInfo(),
            events: [],
            dotStatus: 'success',
        },
        {
            status: options?.isPartialReturnRefused ? RETURN_PARTIALLY_REFUNDED : RETURN_REFUNDED,
            title: options?.isPartialReturnRefused ? messages.orderStatus.partiallyReturnRefunded() : messages.orderStatus.returnRefunded(),
            description: (_: any) => '',
            deliveryTimeInfo: messages.returnTracking.estimatedRefundDateInfo(),
            events: [],
            dotStatus: 'none',
        }
    ]
}) as { [key: string]: any }

export function buildTimeline(trackingEvents: Array<IOrderTrackingEvent>, messages: any, options: TimelineOptions) {
    const { isReturn, isPremierOrder, isReturnNotSupportedInCountry } = options

    let timelineType = 'STANDARD'
    if (isPremierOrder) timelineType = 'PREMIER'
    if (isReturn) {
        if (isReturnNotSupportedInCountry) timelineType = 'RETURNS_WITHOUT_TRACKING'
        else timelineType = 'RETURNS'
    }

    const timelines = TIMELINES(messages, options)

    return timelines[timelineType](trackingEvents)
}

const getReturnProcessingEvents = (messages: any, options: TimelineOptions) => {
    const { returnEvents } = messages
    const acceptedMessage = options?.isPartialReturnRefused ? returnEvents?.['PARTIALLY_RETURN_ACCEPTED']?.() : returnEvents?.['RETURN_ACCEPTED']?.()

    return [
        {
            status: RETURN_RECEIVED,
            description: returnEvents?.['RETURN_RECEIVED']?.()
        },
        {
            status: RETURN_ACCEPTED,
            description: acceptedMessage || ''
        }
    ]; 
}

const getProcessingEventsByCountry = (messages: any, options: TimelineOptions) => {
    const { orderEvents } = messages
    const standardProcessingEvents = [
        {
            status: SCHEDULED,
            description: orderEvents?.['SCHEDULED']?.(),
        },
        {
            status: RELEASED,
            description: orderEvents?.['RELEASED']?.(),
        },
    ]

    if (options?.isJITEnabled) {
        return [
            {
                status: TRANSFERRED,
                description: orderEvents && orderEvents['TRANSFERRED']?.(),
            },
            ...standardProcessingEvents,
        ]
    }

    return standardProcessingEvents
}

export function orderCancelledStatus(messages: any): Array<IOrderTrackingData> {
    return [
        {
            status: CANCELLED,
            title: messages.orderStatus.cancelled(),
            description: (info: any) => messages.orderStatusDesc.cancelled(info),
            events: [],
            dotStatus: 'error',
        },
    ]
}

export function returnRefusedStatus(messages: any): Array<IOrderTrackingData> {
    return [
        {
            status: RETURN_REFUSED,
            title: messages.orderStatus.returnRefused(),
            description: (info: any) => messages.orderStatusDesc.returnRefused(info),
            events: [],
            dotStatus: 'error',
        },
    ]
}
